import { Button } from "@kovernow/react-ui/components/button";
import { useMutationGate } from "../core/mutation-gate";
import { useT } from "../core/use-t";
import { OtpInput } from "./OtpInput";
import { useCountdown } from "./use-countdown";

/**
 * The email-OTP step shared by sign-in, sign-up and reset: self-submitting
 * code field, visible resend countdown, and a way back.
 */
export function OtpStep({
  intro,
  otpResetCount,
  busy,
  error,
  resendAvailableAt,
  onSubmit,
  onResend,
  onBack,
  backLabel,
}: {
  intro: string;
  otpResetCount: number;
  busy: boolean;
  error?: string;
  resendAvailableAt?: number;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  backLabel: string;
}) {
  const t = useT();
  const gate = useMutationGate();
  const seconds = useCountdown(resendAvailableAt);
  const waiting = seconds > 0;
  return (
    <div className="flex flex-col gap-6">
      <p>{intro}</p>
      <OtpInput
        label={t("auth.otp.label")}
        resetKey={otpResetCount}
        busy={busy}
        error={error}
        blocked={gate.blocked}
        onComplete={onSubmit}
      />
      {busy ? <p className="text-sm text-muted-foreground">{t("auth.otp.verifying")}</p> : null}
      <div className="flex flex-col gap-1">
        <Button
          type="button"
          variant="outline"
          size="touch"
          aria-disabled={waiting || gate.blocked ? true : undefined}
          aria-describedby="otp-resend-reason"
          onClick={() => {
            if (!waiting && !gate.blocked) onResend();
          }}
        >
          {t("auth.otp.resend")}
        </Button>
        <p id="otp-resend-reason" className="text-sm text-muted-foreground">
          {waiting ? t("auth.otp.resendIn", { seconds }) : gate.reason ?? ""}
        </p>
      </div>
      <Button type="button" variant="link" className="self-start px-0" onClick={onBack}>
        {backLabel}
      </Button>
    </div>
  );
}
